
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

const ForgotPasswordForm = ({ onBack }: ForgotPasswordFormProps) => {
  const { t } = useTranslation();
  const { resetPassword } = useAuth();
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const success = await resetPassword(forgotPasswordEmail);
      if (success) {
        onBack(); // Return to login view only on success
      }
    } catch (error) {
      // Error handling is now done in the usePasswordReset hook
      console.error('Password reset error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t('auth.forgotPassword.title')}</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="h-8 px-2"
        >
          {t('auth.forgotPassword.backToLogin')}
        </Button>
      </div>
      <form onSubmit={handleForgotPassword} className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="reset-email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t('auth.forgotPassword.emailAddress')}
          </Label>
          <Input 
            id="reset-email" 
            type="email" 
            placeholder={t('auth.forgotPassword.emailPlaceholder')}
            value={forgotPasswordEmail} 
            onChange={(e) => setForgotPasswordEmail(e.target.value)} 
            required 
          />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>{t('auth.forgotPassword.important')}</strong> {t('auth.forgotPassword.instructions')}
          </p>
        </div>
        <Button 
          type="submit" 
          className="w-full bg-kbeauty-brown hover:bg-kbeauty-brown/90 text-white" 
          disabled={isLoading}
        >
          {isLoading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetLink')}
        </Button>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
